import React from 'react';
import { Card, Typography, Row, Col, Statistic } from 'antd';
import { Column, Line } from '@ant-design/charts';
import { 
  RiseOutlined, 
  FallOutlined, 
  LineChartOutlined,
  BarChartOutlined 
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface AnalyticsData {
  timeRange: string;
  dataPoints: number;
  volumeAnalysis: {
    trend: {
      trend: string;
      strength: string;
      confidence: number;
      forecast?: Array<{ period: string; predictedValue: number; confidence: string }>;
    };
    statistics: {
      mean: number;
      median: number;
      min: number;
      max: number;
    };
  };
  latencyAnalysis?: {
    mean: number;
    median: number;
    percentiles: {
      p95: number;
    };
    outliers: {
      count: number;
      percentage: number;
    };
  };
  errorAnalysis: {
    totalErrors: number;
    totalTransactions: number;
    errorRate: number;
  };
  chartData?: {
    volume: Array<{ date: string; count: number }>;
    latency: Array<{ date: string; avgDuration: number }>;
  };
}

interface AnalyticsChartProps {
  data: AnalyticsData;
}

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ data }) => {
  const { volumeAnalysis, latencyAnalysis, errorAnalysis, chartData } = data;

  // Prepare volume chart config
  const volumeConfig = {
    data: chartData?.volume || [],
    xField: 'date',
    yField: 'count',
    height: 200,
    smooth: true,
    color: '#1664FF',
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    xAxis: {
      label: {
        style: {
          fontSize: 10,
        },
        formatter: (text: string) => {
          const date = new Date(text);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fontSize: 10,
        },
      },
    },
    tooltip: {
      formatter: (datum: any) => {
        return {
          name: 'Transactions',
          value: datum.count,
        };
      },
    },
  };

  // Prepare latency chart config
  const latencyConfig = {
    data: chartData?.latency || [],
    xField: 'date',
    yField: 'avgDuration',
    height: 200,
    smooth: true,
    color: '#52C41A',
    point: {
      size: 3,
      shape: 'circle',
    },
    xAxis: {
      label: {
        style: {
          fontSize: 10,
        },
        formatter: (text: string) => {
          const date = new Date(text);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fontSize: 10,
        },
        formatter: (value: number) => `${value}ms`,
      },
    },
    tooltip: {
      formatter: (datum: any) => {
        return {
          name: 'Latency',
          value: `${datum.avgDuration}ms`,
        };
      },
    },
  };

  const getTrendIcon = () => {
    if (volumeAnalysis.trend.trend === 'increasing') {
      return <RiseOutlined style={{ color: '#52C41A', fontSize: 20 }} />;
    } else if (volumeAnalysis.trend.trend === 'decreasing') {
      return <FallOutlined style={{ color: '#FF4D4F', fontSize: 20 }} />;
    } else {
      return <LineChartOutlined style={{ color: '#1677FF', fontSize: 20 }} />;
    }
  };

  return (
    <div className="analytics-chart-container" style={{ marginTop: 16 }}>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small" style={{ background: '#F0F5FF', border: 'none' }}>
            <Statistic
              title={<Text style={{ fontSize: 11 }}>Avg Volume</Text>}
              value={volumeAnalysis.statistics.mean}
              precision={0}
              prefix={getTrendIcon()}
              valueStyle={{ fontSize: 16, fontWeight: 600, color: '#1677FF' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" style={{ background: latencyAnalysis && latencyAnalysis.mean > 2000 ? '#FFF7E6' : '#F6FFED', border: 'none' }}>
            <Statistic
              title={<Text style={{ fontSize: 11 }}>Avg Latency</Text>}
              value={latencyAnalysis?.mean || 0}
              precision={0}
              suffix="ms"
              valueStyle={{ 
                fontSize: 16, 
                fontWeight: 600, 
                color: latencyAnalysis && latencyAnalysis.mean > 2000 ? '#FA8C16' : '#52C41A' 
              }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" style={{ background: errorAnalysis.errorRate > 1 ? '#FFF1F0' : '#F6FFED', border: 'none' }}>
            <Statistic
              title={<Text style={{ fontSize: 11 }}>Error Rate</Text>}
              value={errorAnalysis.errorRate}
              precision={2}
              suffix="%"
              valueStyle={{ 
                fontSize: 16, 
                fontWeight: 600, 
                color: errorAnalysis.errorRate > 1 ? '#FF4D4F' : '#52C41A' 
              }}
            />
          </Card>
        </Col>
      </Row>

      <Card 
        size="small" 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChartOutlined style={{ color: '#1664FF' }} />
            <Text strong style={{ fontSize: 13 }}>Transaction Volume</Text>
          </div>
        }
        style={{ marginBottom: 12 }}
      >
        <Column {...volumeConfig} />
      </Card>

      {chartData?.latency && chartData.latency.length > 0 && (
        <Card 
          size="small" 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LineChartOutlined style={{ color: '#52C41A' }} />
              <Text strong style={{ fontSize: 13 }}>Latency Over Time</Text>
            </div>
          }
        >
          <Line {...latencyConfig} />
        </Card>
      )}

      {volumeAnalysis.trend.forecast && volumeAnalysis.trend.forecast.length > 0 && (
        <Card 
          size="small" 
          title={<Text strong style={{ fontSize: 13 }}>📊 Forecast (Next 3 Periods)</Text>}
          style={{ marginTop: 12, background: '#FAFAFA' }}
        >
          <Row gutter={[8, 8]}>
            {volumeAnalysis.trend.forecast.map((f, idx) => (
              <Col span={8} key={idx}>
                <div style={{ 
                  padding: 8, 
                  background: 'white', 
                  borderRadius: 6,
                  textAlign: 'center',
                  border: '1px solid #E8E8E8'
                }}>
                  <Text type="secondary" style={{ fontSize: 10 }}>Period {f.period}</Text>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#1677FF', margin: '4px 0' }}>
                    {f.predictedValue}
                  </div>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    {f.confidence} confidence
                  </Text>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}
    </div>
  );
};

